import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { uploadOpportunities } from '../../../api/opportunity.api';

function OpportunityImport({ open, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
        setError('');
      } else {
        setError('Please select a valid CSV file.');
        setFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await uploadOpportunities(formData);

      clearInterval(progressInterval);
      setUploadProgress(100);

      setResult(response);

      if (response.success) {
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setUploading(false);
    setUploadProgress(0);
    setResult(null);
    setError('');
    onClose();
  };

  const downloadTemplate = () => {
    const csvContent = [
      ['title', 'description', 'stage', 'status', 'value', 'currency', 'probability', 'expectedCloseDate', 'actualCloseDate', 'products', 'source', 'tags', 'notes'],
      ['New Software License', 'Enterprise software license for 500 users', 'Proposal', 'open', '50000', 'USD', '75', '2024-03-15', '', 'Software License, Support', 'Web', 'enterprise,software', 'High priority deal'],
      ['Consulting Project', 'Implementation consulting for CRM system', 'Negotiation', 'open', '25000', 'USD', '60', '2024-02-28', '', 'Consulting, Training', 'Referral', 'consulting,crm', 'Follow up needed'],
    ].map((row) => row.map((field) => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'opportunities_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          <UploadIcon color="primary" />
          <Typography variant="h6">Import Opportunities</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Instructions */}
          <Alert severity="info" icon={<InfoIcon />}>
            <Typography variant="body2">
              Upload a CSV file with opportunity data. The file should include headers matching the field names.
              Download the template below for the correct format.
            </Typography>
          </Alert>

          {/* Template Download */}
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Download Template
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Use this template to ensure your CSV file has the correct format and headers.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadTemplate}
            >
              Download CSV Template
            </Button>
          </Paper>

          {/* File Upload */}
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Upload CSV File
            </Typography>

            <Box
              sx={{
                border: '2px dashed',
                borderColor: file ? 'success.main' : 'grey.300',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                backgroundColor: file ? 'success.50' : 'grey.50',
                transition: 'all 0.2s',
              }}
            >
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id="csv-file-input"
                disabled={uploading}
              />
              <label htmlFor="csv-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  disabled={uploading}
                  sx={{ mb: 2 }}
                >
                  Select CSV File
                </Button>
              </label>

              {file && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="success.main" fontWeight="medium">
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024).toFixed(1)}
                    {' '}
                    KB
                  </Typography>
                </Box>
              )}

              {!file && (
                <Typography variant="body2" color="text.secondary">
                  Click to select a CSV file or drag and drop
                </Typography>
              )}
            </Box>
          </Paper>

          {/* Error Display */}
          {error && (
            <Alert severity="error" icon={<ErrorIcon />}>
              {error}
            </Alert>
          )}

          {/* Upload Progress */}
          {uploading && (
            <Box>
              <Typography variant="body2" gutterBottom>
                Uploading opportunities...
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" color="text.secondary">
                {uploadProgress}
                % complete
              </Typography>
            </Box>
          )}

          {/* Upload Result */}
          {result && (
            <Alert
              severity={result.success ? 'success' : 'error'}
              icon={result.success ? <CheckCircleIcon /> : <ErrorIcon />}
            >
              <Typography variant="body2" gutterBottom>
                {result.success ? 'Upload completed successfully!' : 'Upload failed'}
              </Typography>

              {result.success && (
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Imported
                    {' '}
                    {result.imported}
                    {' '}
                    opportunities
                  </Typography>

                  {result.errors && result.errors.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="warning.main">
                        {result.errors.length}
                        {' '}
                        rows had errors:
                      </Typography>
                      <List dense>
                        {result.errors.slice(0, 3).map((error, index) => (
                          <ListItem key={index} sx={{ py: 0 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <ErrorIcon color="warning" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`Row ${index + 1}: ${error.error}`}
                              primaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItem>
                        ))}
                        {result.errors.length > 3 && (
                          <ListItem sx={{ py: 0 }}>
                            <ListItemText
                              primary={`... and ${result.errors.length - 3} more errors`}
                              primaryTypographyProps={{ variant: 'caption', color: 'warning.main' }}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Box>
                  )}
                </Box>
              )}
            </Alert>
          )}

          {/* CSV Format Info */}
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              CSV Format Requirements
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your CSV file should include the following columns:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {[
                'title', 'description', 'stage', 'status', 'value',
                'currency', 'probability', 'expectedCloseDate',
                'actualCloseDate', 'products', 'source', 'tags', 'notes',
              ].map((field) => (
                <Chip key={field} label={field} size="small" variant="outlined" />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              * Required fields: title, stage
            </Typography>
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!file || uploading}
          startIcon={uploading ? null : <UploadIcon />}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default OpportunityImport;
